import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Aggrid from "../../../framework/AgGrid";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import GlobalContext from "../../../framework/GlobalContext";
import { getDoctorCommissionDetails } from "../../../services/doctor-commission";
import { rowDown, rowUp, selectRow } from "../../../utils/gridUtils";
import classes from "./index.module.css";

const DoctorCommissionProgramDetails = (props) => {
  const { doctorCommissionId = null, onEscape = () => {} } = props;
  const [doctorCommissionProgramData, setDoctorCommissionProgramData] =
    useState(null);
  const [rowData, setRowData] = useState(null);
  const [api, setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Minimum Amount (₹)",
        field: "min",
        width: 200,
      },
      {
        headerName: "Maximum Amount (₹)",
        field: "max",
        width: 200,
      },
      {
        headerName: "Commission %",
        field: "commissionPercentage",
        width: 200,
        valueGetter: (params) => {
          return params.data?.commissionPercentage;
        },
      },
    ];
  }, []);

  const getDoctorCommissionProgramDetails = useCallback(
    (doctorCommissionId) => {
      getDoctorCommissionDetails(
        `doctorCommissionId=${doctorCommissionId}`
      ).then((resp) => {
        const data = resp?.result?.rows;
        setDoctorCommissionProgramData(data);
        setRowData(data?.doctorCommissionDetails);
      });
    },
    []
  );

  const onGridReadyHandler = useCallback(
    (params) => {
      setApi(params.api);
      params.api.sizeColumnsToFit();
      selectRow(params.api, selectedRowIndex);
    },
    [selectedRowIndex]
  );

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api);
        return;
      }
      if (key === "arrowup") {
        rowUp(api);
        return;
      }
    },
    [api, onEscape]
  );

  useEffect(() => {
    if (doctorCommissionId) {
      getDoctorCommissionProgramDetails(doctorCommissionId);
    }
  }, [getDoctorCommissionProgramDetails, doctorCommissionId]);

  useEffect(() => {
    setCurrentComponent("grid_doctor_commission_program_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_doctor_commission_program_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["doctor-commission-program-details"]}>
      <div className={classes["doctor-commission-program-details-header"]}>
        <div
          className={classes["doctor-commission-program-details-header-left"]}
        >
          <span>
            {doctorCommissionProgramData?.doctorCommissionName?.toUpperCase()} -{" "}
            {doctorCommissionProgramData?.doctorCommissionId}
          </span>
          <span>
            From -{" "}
            {moment(`${doctorCommissionProgramData?.startDate}`).format(
              "DD/MM/YYYY"
            )}
          </span>
          <span>
            To -{" "}
            {moment(`${doctorCommissionProgramData?.endDate}`).format(
              "DD/MM/YYYY"
            )}
          </span>
        </div>
      </div>

      <div className={classes["doctor-commission-program-details-grid"]}>
        <Aggrid
          id="grid_doctor_commission_program_details"
          className="ag-theme-balham"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={(params) => {
            setTimeout(() => {
              onGridReadyHandler(params);
            }, 100);
          }}
          onCellClicked={(params) => {
            setSelectedRowIndex(params.rowIndex);
          }}
        />
      </div>
    </div>
  );
};

export default DoctorCommissionProgramDetails;
